import type {
  BusinessTrip,
  TripDetail,
  TripExpenseTotals,
  TripDetailCity,
  TripDetailCost,
  TripDetailCostDocument,
  TripApprovalStep,
  TripApprovalHistory,
  TripCityDetail,
  TripCityHotel,
  TripCityTransportation,
  TripCityFuel,
  TransportationAssistanceType,
} from '@/hooks/trips/types';

export interface RawTripListItem {
  id: number | string;
  name?: string | null;
  request_number?: string | null;
  creator_id?: number | string | null;
  creator_name?: string | null;
  initiator_id?: number | string | null;
  initiator_name?: string | null;
  trip_type?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  state?: string | null;
  cities_count?: number | null;
  approved_at?: string | null;
  submitted_at?: string | null;
  my_state?: string | null;
  roaming_request?: boolean | null;
  roaming_activated?: boolean | null;
  available_actions?: string[] | null;
}

const STATUS_MAP: Record<string, BusinessTrip['status']> = {
  draft: 'draft',
  pending: 'pending',
  approved: 'approved',
  declined: 'declined',
  returned_back: 'returned_back',
  cancelled: 'cancelled',
  extended: 'extended',
  shortened: 'shortened',
  finalizing: 'finalizing',
  finished: 'finished',
  // Legacy fallbacks
  rejected: 'declined',
  to_approve: 'pending',
  to_edit: 'returned_back',
  to_correct: 'returned_back',
  corrected: 'finalizing',
};

export function mapTripStatus(state: string | null | undefined): BusinessTrip['status'] {
  if (!state) {
    return 'unknown';
  }

  const key = state.toLowerCase();
  return STATUS_MAP[key] ?? 'unknown';
}

export function mapTripListItem(raw: RawTripListItem): BusinessTrip {
  const initiatorName = raw.initiator_name ?? raw.creator_name ?? '—';
  const authorName = raw.creator_name ?? raw.initiator_name ?? '—';
  const requestNumber =
    typeof raw.request_number === 'string' && raw.request_number.trim().length > 0
      ? raw.request_number
      : undefined;
  const submittedAt =
    typeof raw.submitted_at === 'string' && raw.submitted_at.trim().length > 0
      ? raw.submitted_at
      : undefined;
  const myState =
    typeof raw.my_state === 'string' && raw.my_state.trim().length > 0
      ? mapTripStatus(raw.my_state)
      : undefined;
  const approvedAt =
    typeof raw.approved_at === 'string' && raw.approved_at.trim().length > 0
      ? raw.approved_at
      : undefined;
  const tripType: '' | 'local' | 'international' =
    raw.trip_type === 'local' || raw.trip_type === 'international'
      ? (raw.trip_type as 'local' | 'international')
      : '';
  const roamingExpense = Boolean(raw.roaming_request);
  const roamingActivated = raw.roaming_activated === true;
  const availableActions = Array.isArray(raw.available_actions)
    ? raw.available_actions.filter((action) => typeof action === 'string')
    : undefined;

  const trip: BusinessTrip = {
    id: String(raw.id ?? ''),
    number: requestNumber ?? String(raw.id ?? ''),
    requestNumber,
    initiator: {
      id: String(raw.initiator_id ?? raw.creator_id ?? ''),
      name: initiatorName,
    },
    author: {
      id: String(raw.creator_id ?? raw.initiator_id ?? ''),
      name: authorName,
    },
    startDate: raw.start_date ?? '',
    endDate: raw.end_date ?? '',
    cityCount: Number(raw.cities_count ?? 0),
    status: mapTripStatus(raw.state),
    tripType,
    submittedAt,
    approvedAt,
    ...(myState ? { myState } : {}),
    roamingExpense,
    roamingActivated,
    ...(availableActions ? { availableActions } : {}),
  };

  return trip;
}

export interface RawTripDetail extends RawTripListItem {
  substitute_employee_id?: number | string | null;
  substitute_employee_name?: string | null;
  purpose?: string | null;
  comment?: string | null;
  roaming_request?: boolean | null;
  roaming_limit_amount?: number | null;
  roaming_mobile_number?: string | null;
  roaming_activated?: boolean | null;
  need_daily_payout?: boolean | null;
  daily_payout_amount?: number | null;
  total_daily_payout_amount?: number | null;
  total_amount?: number | null;
  total_hotel_amount?: number | null;
  total_transportation_amount?: number | null;
  total_fuel_amount?: number | null;
  total_cost?: number | null;
  total_transportation_cost?: number | null;
  total_hotel_cost?: number | null;
  total_change?: number | null;
  trip_days?: number | null;
  permanent_residence_country_id?: number | string | null;
  permanent_residence_country_name?: string | null;
  permanent_residence_city_id?: number | string | null;
  permanent_residence_city_name?: string | null;
  pending_approval_step_name?: string | null;
  pending_approver_id?: number | null;
  pending_approver_name?: string | null;
  cities?: unknown[] | null;
  costs?: unknown[] | null;
  approval_steps?: unknown[] | null;
  approval_history?: unknown[] | null;
  parent_id?: number | string | null;
  parent_request_number?: string | null;
  parent_state?: string | null;
  parent_state_label?: string | null;
  available_actions?: string[] | null;
}

function buildExpenseTotals(raw: RawTripDetail): TripExpenseTotals {
  const total = Number(raw.total_amount ?? 0);
  const transport = Number(raw.total_transportation_amount ?? 0);
  const accommodation = Number(raw.total_hotel_amount ?? 0);
  const fuel = Number(raw.total_fuel_amount ?? 0);
  const roaming = Number(raw.roaming_limit_amount ?? 0);
  const daily = Number(raw.total_daily_payout_amount ?? raw.daily_payout_amount ?? 0);

  const computedTotal = total || transport + accommodation + fuel + roaming + daily;

  return {
    total: computedTotal,
    transport,
    accommodation,
    fuel,
    roaming,
    daily,
  };
}

export function mapTripDetailCities(rawCities: unknown): TripDetailCity[] {
  if (!Array.isArray(rawCities)) {
    return [];
  }

  return rawCities
    .map((city) => {
      if (!city || typeof city !== 'object') {
        return null;
      }

      const record = city as Record<string, unknown>;

      return {
        id: Number(record.id ?? 0),
        countryId: Number(record.country_id ?? 0),
        countryName: String(record.country_name ?? ''),
        cityId: Number(record.city_id ?? 0),
        cityName: String(record.city_name ?? ''),
        arrivalDate: String(record.arrival_date ?? ''),
        departureDate: String(record.departure_date ?? ''),
        needHotel: Boolean(record.need_hotel),
        needTransportation: Boolean(record.need_transportation),
        needFuel: Boolean(record.need_fuel),
        daysCount: Number(record.days_count ?? 0),
        totalHotelAmount: Number(record.total_hotel_amount ?? 0),
        totalTransportationAmount: Number(record.total_transportation_amount ?? 0),
        totalFuelAmount: Number(record.total_fuel_amount ?? 0),
        totalAmount: Number(record.total_amount ?? 0),
        hotels: mapCityHotels(record.hotels ?? record.hotel_ids ?? []),
        transportations: mapCityTransportations(
          record.transportations ?? record.transportation_ids ?? []
        ),
        fuels: mapCityFuels(record.fuels ?? record.fuel_ids ?? []),
      } as TripDetailCity;
    })
    .filter((value): value is TripDetailCity => Boolean(value));
}

export function mapTripDetailCity(rawCity: unknown): TripDetailCity {
  const [mappedCity] = mapTripDetailCities(Array.isArray(rawCity) ? rawCity : [rawCity]);

  if (!mappedCity) {
    throw new Error('City payload is malformed.');
  }

  return mappedCity;
}

function mapTransportationAssistanceType(value: unknown): TransportationAssistanceType | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  if (value === 'ticket_or_advance' || value === 'rental_car' || value === 'car_with_driver') {
    return value;
  }

  return undefined;
}

function mapCityHotels(rawHotels: unknown): TripCityHotel[] {
  if (!Array.isArray(rawHotels)) {
    return [];
  }

  return rawHotels
    .map((hotel) => {
      if (!hotel || typeof hotel !== 'object') {
        return null;
      }

      const record = hotel as Record<string, unknown>;

      return {
        id: Number(record.id ?? 0),
        checkInDate: String(record.check_in_date ?? ''),
        checkOutDate: String(record.check_out_date ?? ''),
        bookingSupport: Boolean(record.booking_support),
        isBooked: Boolean(record.is_booked),
        prepaymentAmount: Number(record.prepayment_amount ?? 0),
        prepaymentCurrencyId: Number(record.prepayment_currency_id ?? 0),
        prepaymentCurrencyName: String(record.prepayment_currency_name ?? ''),
        prepaymentDate:
          typeof record.prepayment_date === 'string' ? record.prepayment_date : undefined,
        contractedHotel: Boolean(record.contracted_hotel),
        dailyAmount: Number(record.daily_amount ?? 0),
        daysCount: Number(record.days_count ?? 0),
        totalAmount: Number(record.total_amount ?? 0),
      } as TripCityHotel;
    })
    .filter((value): value is TripCityHotel => Boolean(value));
}

function mapCityTransportations(rawTransportations: unknown): TripCityTransportation[] {
  if (!Array.isArray(rawTransportations)) {
    return [];
  }

  return rawTransportations
    .map((transportation) => {
      if (!transportation || typeof transportation !== 'object') {
        return null;
      }

      const record = transportation as Record<string, unknown>;

      const assistanceType = mapTransportationAssistanceType(record.assistance_type);
      const assistanceTypeLabel =
        typeof record.assistance_type_label === 'string' ? record.assistance_type_label : undefined;

      return {
        id: Number(record.id ?? 0),
        companyName: String(record.company_name ?? ''),
        bookingSupport: Boolean(record.booking_support),
        isBooked: Boolean(record.is_booked),
        prepaymentAmount: Number(record.prepayment_amount ?? 0),
        prepaymentCurrencyId: Number(record.prepayment_currency_id ?? 0),
        prepaymentCurrencyName: String(record.prepayment_currency_name ?? ''),
        prepaymentDate:
          typeof record.prepayment_date === 'string' ? record.prepayment_date : undefined,
        amount: Number(record.amount ?? 0),
        assistanceType,
        assistanceTypeLabel,
      } as TripCityTransportation;
    })

    .filter((value): value is TripCityTransportation => Boolean(value));
}

function mapCityFuels(rawFuels: unknown): TripCityFuel[] {
  if (!Array.isArray(rawFuels)) {
    return [];
  }

  return rawFuels
    .map((fuel) => {
      if (!fuel || typeof fuel !== 'object') {
        return null;
      }

      const record = fuel as Record<string, unknown>;

      return {
        id: Number(record.id ?? 0),
        fuelTypeId: Number(record.fuel_type_id ?? 0),
        fuelTypeName: String(record.fuel_type_name ?? ''),
        carModel: typeof record.car_model === 'string' ? record.car_model : undefined,
        fuelAmount: Number(record.fuel_amount ?? 0),
      } as TripCityFuel;
    })
    .filter((value): value is TripCityFuel => Boolean(value));
}

export function mapTripCityDetail(rawCity: unknown): TripCityDetail {
  const baseCity = mapTripDetailCity(rawCity);
  const record = (rawCity ?? {}) as Record<string, unknown>;

  return {
    ...baseCity,
    hotels: mapCityHotels(record.hotels ?? record.hotel_ids ?? []),
    transportations: mapCityTransportations(
      record.transportations ?? record.transportation_ids ?? []
    ),
    fuels: mapCityFuels(record.fuels ?? record.fuel_ids ?? []),
  };
}

function mapTripDetailCosts(rawCosts: unknown): TripDetailCost[] {
  if (!Array.isArray(rawCosts)) {
    return [];
  }

  return rawCosts
    .map((cost) => {
      if (!cost || typeof cost !== 'object') {
        return null;
      }

      const record = cost as Record<string, unknown>;
      const rawDocuments = (record.documents ?? record.document_ids) as unknown;
      const documents = Array.isArray(rawDocuments)
        ? (rawDocuments as unknown[])
            .map((doc) => {
              if (!doc || typeof doc !== 'object') {
                return null;
              }

              const docRecord = doc as Record<string, unknown>;
              const sizeValue = Number(docRecord.size ?? Number.NaN);
              const rawName =
                typeof docRecord.name === 'string' && docRecord.name.trim().length > 0
                  ? (docRecord.name as string)
                  : typeof docRecord.filename === 'string'
                    ? (docRecord.filename as string)
                    : '';
              return {
                id: Number(docRecord.id ?? 0),
                name: rawName,
                mimetype:
                  typeof docRecord.mimetype === 'string'
                    ? (docRecord.mimetype as string)
                    : undefined,
                size: Number.isFinite(sizeValue) ? sizeValue : undefined,
                sizeHuman:
                  typeof docRecord.size_human === 'string'
                    ? (docRecord.size_human as string)
                    : undefined,
              } as TripDetailCostDocument;
            })
            .filter((value): value is TripDetailCostDocument => {
              if (!value) {
                return false;
              }
              return value.name.trim().length > 0;
            })
        : [];

      return {
        id: Number(record.id ?? 0),
        costType: String(record.cost_type ?? ''),
        costTypeLabel:
          typeof record.cost_type_label === 'string' ? record.cost_type_label : undefined,
        actualCost: Number(record.actual_cost ?? 0),
        currencyId: Number(record.currency_id ?? 0),
        currencyName: String(record.currency_name ?? ''),
        documents,
        comment: typeof record.comment === 'string' ? record.comment : undefined,
      } as TripDetailCost;
    })
    .filter((value): value is TripDetailCost => Boolean(value));
}

function mapTripApprovalSteps(rawSteps: unknown): TripApprovalStep[] {
  if (!Array.isArray(rawSteps)) {
    return [];
  }

  return rawSteps
    .map((step) => {
      if (!step || typeof step !== 'object') {
        return null;
      }

      const record = step as Record<string, unknown>;
      return {
        id: Number(record.id ?? 0),
        stepName: String(record.step_name ?? ''),
        approverId: Number(record.approver_id ?? 0),
        approverName: String(record.approver_name ?? ''),
        approverPosition:
          typeof record.approver_position === 'string' ? record.approver_position : undefined,
        state: String(record.state ?? ''),
        actionDate: typeof record.action_date === 'string' ? record.action_date : undefined,
        comments: typeof record.comments === 'string' ? record.comments : undefined,
      } as TripApprovalStep;
    })
    .filter((value): value is TripApprovalStep => Boolean(value));
}

function mapTripApprovalHistory(rawHistory: unknown): TripApprovalHistory[] {
  if (!Array.isArray(rawHistory)) {
    return [];
  }

  return rawHistory
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const record = entry as Record<string, unknown>;
      return {
        id: Number(record.id ?? 0),
        actionType: String(record.action_type ?? ''),
        actionUserId: Number(record.action_user_id ?? 0),
        actionUserName: String(record.action_user_name ?? ''),
        actionSummary:
          typeof record.action_summary === 'string' ? record.action_summary : undefined,
        actionUserRole:
          typeof record.action_user_role === 'string' ? record.action_user_role : undefined,
        actionUserRoleLabel:
          typeof record.action_user_role_label === 'string'
            ? record.action_user_role_label
            : undefined,
        actionUserPosition:
          typeof record.action_user_position === 'string' ? record.action_user_position : undefined,
        actionDate: typeof record.action_date === 'string' ? record.action_date : undefined,
        comments: typeof record.comments === 'string' ? record.comments : undefined,
      } as TripApprovalHistory;
    })
    .filter((value): value is TripApprovalHistory => Boolean(value));
}

export function mapTripDetail(raw: RawTripDetail): TripDetail {
  const authorName = raw.creator_name ?? raw.initiator_name ?? '—';
  const initiatorName = raw.initiator_name ?? authorName;
  const actingEmployeeId = raw.substitute_employee_id;
  const actingEmployeeName = raw.substitute_employee_name ?? undefined;
  const requestNumber =
    typeof raw.request_number === 'string' && raw.request_number.trim().length > 0
      ? raw.request_number
      : String(raw.id ?? '');
  const submittedAt =
    typeof raw.submitted_at === 'string' && raw.submitted_at.trim().length > 0
      ? raw.submitted_at
      : undefined;
  const approvedAt =
    typeof raw.approved_at === 'string' && raw.approved_at.trim().length > 0
      ? raw.approved_at
      : undefined;
  const parentId =
    raw.parent_id !== null &&
    raw.parent_id !== undefined &&
    raw.parent_id !== '' &&
    raw.parent_id !== 0
      ? String(raw.parent_id)
      : undefined;
  const parentRequestNumber =
    typeof raw.parent_request_number === 'string' && raw.parent_request_number.trim().length > 0
      ? raw.parent_request_number.trim()
      : undefined;
  const parentState =
    typeof raw.parent_state === 'string' && raw.parent_state.trim().length > 0
      ? mapTripStatus(raw.parent_state)
      : undefined;
  const parentStateLabel =
    typeof raw.parent_state_label === 'string' && raw.parent_state_label.trim().length > 0
      ? raw.parent_state_label.trim()
      : undefined;
  const availableActions = Array.isArray(raw.available_actions)
    ? raw.available_actions.filter((action) => typeof action === 'string')
    : undefined;

  const detail: TripDetail = {
    id: String(raw.id ?? ''),
    number: requestNumber,
    requestNumber,
    parentId,
    parentRequestNumber,
    tripName: raw.name ?? '',
    tripType: (raw.trip_type as 'local' | 'international') ?? 'local',
    initiator: {
      id: String(raw.initiator_id ?? ''),
      name: initiatorName,
    },
    author: {
      id: String(raw.creator_id ?? ''),
      name: authorName,
    },
    actingEmployee:
      actingEmployeeId != null
        ? { id: String(actingEmployeeId), name: actingEmployeeName ?? '' }
        : null,
    startDate: raw.start_date ?? '',
    endDate: raw.end_date ?? '',
    cityCount: Number(raw.cities_count ?? (Array.isArray(raw.cities) ? raw.cities.length : 0)),
    status: mapTripStatus(raw.state),
    submittedAt,
    approvedAt,
    purpose: raw.purpose ?? '',
    comment: raw.comment ?? undefined,
    additionalComment: raw.comment ?? undefined,
    roamingExpense: Boolean(raw.roaming_request),
    roamingAmount: raw.roaming_limit_amount != null ? String(raw.roaming_limit_amount) : undefined,
    roamingActivated: raw.roaming_activated === true,
    phoneNumber: raw.roaming_mobile_number ?? undefined,
    accommodationExpense: Boolean(raw.need_daily_payout),
    accommodationAmount:
      raw.daily_payout_amount != null ? String(raw.daily_payout_amount) : undefined,
    expenseTotals: buildExpenseTotals(raw),
    advance: raw.total_change ?? undefined,
    tripDays: raw.trip_days ?? undefined,
    permanentResidenceCountryId:
      raw.permanent_residence_country_id != null
        ? Number(raw.permanent_residence_country_id)
        : undefined,
    permanentResidenceCityId:
      raw.permanent_residence_city_id != null ? Number(raw.permanent_residence_city_id) : undefined,
    permanentResidenceCountryName: raw.permanent_residence_country_name ?? undefined,
    permanentResidenceCityName: raw.permanent_residence_city_name ?? undefined,
    pendingApprovalStepName: raw.pending_approval_step_name ?? undefined,
    pendingApproverId: raw.pending_approver_id ?? undefined,
    pendingApproverName: raw.pending_approver_name ?? undefined,
    cities: mapTripDetailCities(raw.cities ?? []),
    costs: mapTripDetailCosts(raw.costs ?? []),
    approvalSteps: mapTripApprovalSteps(raw.approval_steps ?? []),
    approvalHistory: mapTripApprovalHistory(raw.approval_history ?? []),
    availableActions,
    parentState,
    parentStateLabel,
  };

  return detail;
}
